import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Loader from '@justgiving/loading/es/Loading';
import { captureException } from '@sentry/browser';
import { Form, Formik, FormikHelpers } from 'formik';

import {
  FitnessApplicationActivityType,
  FitnessApplicationSettingsInput,
  MeasurementSystem,
  PageTotalsDisplayOptions,
} from '../../../../types/__generated__';
import { GetStravaSettingsQuery } from '../../../../types/__generated__';
import { ConnectionStatus } from '../../../hooks/fitness/useFitnessConnection';
import useSaveStravaSettings from '../../../hooks/fitness/useSaveStravaSettings';
import { useApp } from '../../../hooks/useApp';
import useFullPageQuery from '../../../hooks/useFullPageQuery';
import usePageViewTracking from '../../../hooks/usePageViewTracking';
import Button from '../../Button/Button';
import { FormikChoiceGroup as ChoiceGroup } from '../ChoiceGroup';
import { Fieldset } from '../Fieldset';
import { ModalType } from '../StravaModal';
import { FormikSwitch as Switch } from '../Switch';
import getStravaSettings from './stravaSettings.gql';
import { defaultStravaSettings } from './StravaSettingsModalConsts';
import SettingsModal from '../../SettingsModal/SettingsModal';

export type FitnessSettingsFormValues = {
  __typename?: string;
  subscribedActivities: FitnessApplicationActivityType[] | [];
  measurementSystem: MeasurementSystem;
  showDistance: boolean;
  showDuration: boolean;
  showElevation: boolean;
  showPhotos: boolean;
  showMap: boolean;
  showTotaliser: boolean;
};

export type Strava = {
  __typename?: string;
  settings: FitnessSettingsFormValues;
};

export const createStravaInput = (
  values: FitnessSettingsFormValues,
  id: string,
) => {
  return { pageId: id, name: 'Strava', ...values };
};

interface SettingsModalProps {
  connectionStatus?: ConnectionStatus;
  onClose: () => void;
  onModalTypeChange: (type: ModalType) => void;
}

export const StravaSettingsModal = React.forwardRef(
  (
    { onClose, onModalTypeChange, connectionStatus }: SettingsModalProps,
    ref?: React.Ref<HTMLDivElement>,
  ) => {
    const { t } = useTranslation('common', { useSuspense: false });
    const { data } = useFullPageQuery<GetStravaSettingsQuery>(
      getStravaSettings,
      {
        context: {
          credentials: 'include',
        },
      },
    );
    const [setFitnessApplicationSettings] = useSaveStravaSettings();
    const { showToast } = useApp();

    const { title, id: pageId, strava, totals } = data!.page!;

    const subscribedActivities = strava?.settings?.subscribedActivities;
    const isStravaPaused = subscribedActivities?.length === 0;
    const hasSettings = Boolean(strava);

    useEffect(() => {
      if (!hasSettings) {
        const input: FitnessApplicationSettingsInput = createStravaInput(
          defaultStravaSettings,
          pageId,
        );
        setFitnessApplicationSettings(input);
      }
    }, [hasSettings, pageId, setFitnessApplicationSettings]);

    const onSubmit = async (
      values: FitnessSettingsFormValues,
      { setSubmitting }: FormikHelpers<FitnessSettingsFormValues>,
    ) => {
      if (!values.subscribedActivities.length && subscribedActivities?.length) {
        onModalTypeChange('confirmation');
        return;
      }

      const input: FitnessApplicationSettingsInput = createStravaInput(
        values,
        pageId,
      );

      setSubmitting(true);
      try {
        await setFitnessApplicationSettings(input);

        const message = (() => {
          if (
            !hasSettings ||
            (connectionStatus === 'success' &&
              localStorage.getItem('strava.initialFormSave'))
          ) {
            return t('strava.connection.success');
          }
          return t('strava.update.success');
        })();

        showToast({
          message,
          type: 'success',
          dismissAfter: 5000,
        });
      } catch (error) {
        showToast({
          message: t('strava.update.warning'),
          type: 'warning',
          dismissAfter: 10000,
        });
        captureException(error);
      }
      localStorage.removeItem('strava.initialFormSave');
      setSubmitting(false);
      onClose();
    };

    const manageStravaSettings = (strava: Strava) => {
      if (strava && strava.settings) {
        const { __typename, ...userStravaSettings } = strava.settings;
        return userStravaSettings;
      }
      return defaultStravaSettings;
    };

    usePageViewTracking({
      event: 'page view',
      subtype: 'manage strava settings',
      page_section: 'strava',
    });

    return (
      <SettingsModal
        title={title}
        onCloseRequest={onClose}
        aria-label={t('settings.fitness.label')}
      >
        <div ref={ref}>
          <div className="jg-display-flex jg-flex-aic jg-flex-column jg-flex-jcc jg-space-pbms">
            <div className="jg-h1 jg-space-mbmd">
              {t('settings.fitness.title')}
            </div>
            <div className="jg-text--large-regular jg-space-mbml">
              {t('settings.fitness.desc-part-1')}
            </div>
            <div className="jg-text--large-regular">
              {t('settings.fitness.desc-part-2')}
            </div>
          </div>

          <Formik
            onSubmit={onSubmit}
            enableReinitialize={true}
            initialValues={manageStravaSettings(strava as Strava)}
          >
            {({ isValid, isSubmitting }) => (
              <Form>
                <div className="jg-display-flex jg-flex-ais jg-flex-column jg-flex-jcc jg-space-ptml jg-space-pbms">
                  <ChoiceGroup
                    label={t('settings.fitness.activityTypes')}
                    error={
                      isStravaPaused
                        ? t('settings.fitness.errorPaused')
                        : undefined
                    }
                    name="subscribedActivities"
                    multiple={true}
                    choices={[
                      {
                        label: t('settings.fitness.run'),
                        value: FitnessApplicationActivityType.Run,
                      },
                      {
                        label: t('settings.fitness.ride'),
                        value: FitnessApplicationActivityType.Ride,
                      },
                      {
                        label: t('settings.fitness.walk'),
                        value: FitnessApplicationActivityType.Walk,
                      },
                      {
                        label: t('settings.fitness.swim'),
                        value: FitnessApplicationActivityType.Swim,
                      },
                    ]}
                  />
                </div>
                <div className="jg-display-flex jg-flex-ais jg-flex-column jg-flex-jcc jg-space-ptsm jg-space-pbsm">
                  <ChoiceGroup
                    label={t('settings.fitness.measurementSystem')}
                    name="measurementSystem"
                    choices={[
                      {
                        label: t('settings.fitness.imperial'),
                        value: MeasurementSystem.Imperial,
                      },
                      {
                        label: t('settings.fitness.metric'),
                        value: MeasurementSystem.Metric,
                      },
                    ]}
                  />
                </div>
                <div className="jg-display-flex jg-flex-ais jg-flex-column jg-flex-jcc jg-space-ptsm jg-space-pbsm">
                  <Fieldset legend={t('settings.fitness.displayPreferences')}>
                    <Switch
                      name="showDistance"
                      label={t('settings.fitness.showDistance')}
                      hint={t('settings.fitness.showDistance-description')}
                    />
                    <Switch
                      name="showDuration"
                      label={t('settings.fitness.showDuration')}
                      hint={t('settings.fitness.showDuration-description')}
                    />
                    <Switch
                      name="showElevation"
                      label={t('settings.fitness.showElevation')}
                      hint={t('settings.fitness.showElevation-description')}
                    />
                    <Switch
                      name="showPhotos"
                      label={t('settings.fitness.showPhotos')}
                      hint={t('settings.fitness.showPhotos-description')}
                    />
                    <Switch
                      name="showMap"
                      label={t('settings.fitness.showMap')}
                      hint={t('settings.fitness.showActivityMap')}
                    />
                    {totals?.displayOptions !==
                      PageTotalsDisplayOptions.DoNotDisplay && (
                      <Switch
                        name="showTotaliser"
                        label={t('settings.fitness.showTotaliser')}
                        hint={t('settings.fitness.showTotaliser.description')}
                      />
                    )}
                  </Fieldset>
                </div>

                <div>
                  <Button
                    disabled={isSubmitting || !isValid}
                    type="submit"
                    trackingData={{
                      subtype: 'button',
                      event: 'link click',
                      page_section: 'strava',
                    }}
                  >
                    {isSubmitting ? (
                      <Loader size="small" colour="white" />
                    ) : (
                      t('settings.fitness.save')
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>

          {!isStravaPaused && (
            <Button
              className="jg-btn--ghost jg-space-mtms"
              onClick={() => onModalTypeChange('confirmation')}
              type="button"
              trackingData={{
                subtype: 'button',
                event: 'link click',
                page_section: 'strava',
              }}
            >
              {t('settings.fitness.stopSharing')}
            </Button>
          )}
        </div>
      </SettingsModal>
    );
  },
);

StravaSettingsModal.displayName = 'StravaSettingsModal';
